import React, { useState, useMemo, FC } from 'react';
import CustomModal from 'shared/components/andtComponents/Modal';
import Button from 'shared/components/andtComponents/Button';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { GovernanceTagsModalProps } from '../../types';
import styles from './GovernanceTagsModal.module.scss';
import { ReactComponent as AddEditModalIcon } from '../../assets/modalIcon.svg';
import { useGovernanceTags } from '../../hooks/useGovernanceTags';
import { useTagManagement } from './hooks/useTagManagement';
import { ModalHeader, TagsHeader, TagRow, DeleteConfirmationModal } from './components';

const DeleteIcon = () => <GenerateIcon iconName={ICONS.deleteRegular.name} />;

const GovernanceTagsModal: FC<GovernanceTagsModalProps> = ({ isOpen, onClose, resource, onSaveTags, onDeleteTags }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const { distinctTagValues, isLoadingDistinctTagValues } = useGovernanceTags();

  const availableTagKeys = useMemo(() => {
    return distinctTagValues ? Object.keys(distinctTagValues) : [];
  }, [distinctTagValues]);

  const {
    tags,
    isLoading: isTagOperationLoading,
    selectAll,
    selectedTagIndices,
    hasEmptyTag,
    canSave,
    hasRemovedAllExistingTags,
    handleAddTag,
    handleTagChange,
    handleRemoveTag,
    handleSelectTag,
    handleCreateNewKey,
    handleCreateNewValue,
    handleSave: originalHandleSave,
    handleDelete,
    handleSelectAll,
    handleTagSelect,
  } = useTagManagement({
    resource,
    isOpen,
    onSaveTags,
    onDeleteTags,
    onClose,
  });

  const getFilteredTagKeysForRow = useMemo(() => {
    return (currentIndex: number) => {
      const usedKeys = tags
        .map((tag, index) => ({ key: tag.name, index }))
        .filter(({ key, index }) => key && key.trim() !== '' && index !== currentIndex)
        .map(({ key }) => key);

      return availableTagKeys.filter((key) => !usedKeys.includes(key));
    };
  }, [tags, availableTagKeys]);

  const getAvailableTagValuesForKey = useMemo(() => {
    return (tagKey: string): string[] => {
      if (!distinctTagValues || !tagKey) return [];
      return distinctTagValues[tagKey] || [];
    };
  }, [distinctTagValues]);

  const isLoading = isLoadingDistinctTagValues || isTagOperationLoading;

  const handleDeleteSelected = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsSaveLoading(true);
    try {
      await handleDelete();
      setIsDeleteModalOpen(false);
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaveLoading(true);
    try {
      await originalHandleSave();
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleSaveClick = () => {
    if (hasRemovedAllExistingTags) {
      setIsDeleteModalOpen(true);
    } else {
      handleSave();
    }
  };

  const modalTitle = <ModalHeader resource={resource} />;

  const canDeleteSelected = selectedTagIndices.size > 0;

  const footerMessage = canDeleteSelected ? (
    <Button
      text={`Remove Selected (${selectedTagIndices.size})`}
      automationid="delete-selected-tags"
      icon={DeleteIcon}
      onClick={handleDeleteSelected}
      className={styles.removeAllButton}
      disabled={isLoading || isSaveLoading}
    />
  ) : null;

  return (
    <>
      <CustomModal
        open={isOpen}
        onClose={onClose}
        title={modalTitle}
        overrideStyles={{ width: 780 }}
        saveDisabled={!canSave || isLoading}
        onSave={handleSaveClick}
        headerMode="add"
        headerIcon={<AddEditModalIcon />}
        isLoading={isSaveLoading}
        footerMessage={footerMessage}
      >
        <div className={styles.modalContent}>
          <TagsHeader
            selectAll={selectAll}
            tagsLength={tags.length}
            hasEmptyTag={hasEmptyTag}
            onSelectAll={handleSelectAll}
            onAddTag={handleAddTag}
          />
          <div className={styles.tagsContainer}>
            {tags.map((tag, index) => (
              <TagRow
                key={`${tag.name || 'empty'}-${tag.value || 'empty'}-${index}`}
                tag={tag}
                index={index}
                isSelected={selectedTagIndices.has(index)}
                isLoading={isLoading}
                availableTagKeys={getFilteredTagKeysForRow(index)}
                availableTagValues={getAvailableTagValuesForKey(tag.name)}
                onTagChange={handleTagChange}
                onTagSelect={handleSelectTag}
                onRemoveTag={handleRemoveTag}
                onSelectTag={handleTagSelect}
                onCreateNewKey={handleCreateNewKey}
                onCreateNewValue={handleCreateNewValue}
              />
            ))}
          </div>
        </div>
      </CustomModal>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isLoading}
        selectedTagIndices={selectedTagIndices}
        totalTags={tags.length}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default GovernanceTagsModal;
